import React, { memo, useCallback, useState } from 'react';
import { useToastStore } from 'store/toastStore';
import ConfirmDeleteModal from '../DeleteModel';

interface DeleteButtonProps {
    id: number;
    name: string;
    onDelete: (id: number) => Promise<string>;
    disabled?: boolean;
}

const DeleteButton: React.FC<DeleteButtonProps> = memo(({ id, name, onDelete, disabled = false }) => {
    const { showToast } = useToastStore();
    const [isDeleting, setIsDeleting] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const handleDelete = useCallback(async () => {
        setIsDeleting(true);
        const response = await onDelete(id);
        const isError = response === "Action cannot be completed. Please ensure at least one active configuration is selected." ||
                        response.includes("error") || response.includes("cannot");
        showToast(
            response || `The configuration '${name}' has been processed.`,
            isError ? 'error' : 'success'
        );
        setIsDeleting(false);
        setIsModalOpen(false);
    }, [id, name, onDelete, showToast]);

    const handleConfirm = () => {
        handleDelete();
    };

    const handleCancel = () => {
        setIsModalOpen(false);
    };

    return (
        <>
            <button
                className={`relative w-8 h-8 rounded-md border border-blue-300 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 ${
                    (disabled || isDeleting) ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                title="Delete configuration"
                aria-label={`Delete configuration ${name}`}
                onClick={() => setIsModalOpen(true)}
                disabled={disabled || isDeleting}
            >
                {isDeleting ? (
                    <span className="absolute inset-0 flex items-center justify-center">
                        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                    </span>
                ) : (
                    <span className="st-delete text-red-600 text-xs" />
                )}
            </button>
            <ConfirmDeleteModal
                isOpen={isModalOpen}
                configurationName={name}
                onConfirm={handleConfirm}
                onCancel={handleCancel}
            />
        </>
    );
});

DeleteButton.displayName = 'DeleteButton';
export default DeleteButton;